import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import {
  Search, ArrowRight, Users, Package, FileText, Receipt, Factory,
  ShoppingCart, Building2, TrendingUp, LayoutDashboard, Plus, Loader2,
} from "lucide-react";
import { ModalOverlay } from "@/components/ModalOverlay";
import { usePerms } from "@/PermissionContext";
import { GROUPS, navigationAllowed } from "@/navigation";

/**
 * Command Palette (⌘K / Ctrl+K).
 *
 * Universal search + jump-to for customers, products, work orders,
 * invoices, quotations, receipts, purchase orders + hard-coded quick
 * navigations.  Debounced backend lookup with graceful loading state.
 */
const QUICK = [
  { label: "Go to Dashboard",       to: "/",                icon: LayoutDashboard, kind: "nav" },
  { label: "Customers",             to: "/customers",       icon: Users,           kind: "nav" },
  { label: "Products",              to: "/templates",       icon: Package,         kind: "nav" },
  { label: "Work Orders",           to: "/work-orders",     icon: Factory,         kind: "nav" },
  { label: "Sales Invoices",        to: "/invoices",        icon: FileText,        kind: "nav" },
  { label: "Quotations",            to: "/quotations",      icon: FileText,        kind: "nav" },
  { label: "Receipts",              to: "/receipts",        icon: Receipt,         kind: "nav" },
  { label: "Purchase Orders",       to: "/purchase-orders", icon: ShoppingCart,    kind: "nav" },
  { label: "Vendors",               to: "/vendors",         icon: Building2,       kind: "nav" },
  { label: "Reports",               to: "/reports",         icon: TrendingUp,      kind: "nav" },
  { label: "Create · New Quotation",   to: "/quotations/new",     icon: Plus, kind: "action" },
  { label: "Create · New Invoice",     to: "/invoices/new",       icon: Plus, kind: "action" },
  { label: "Create · New Receipt",     to: "/receipts/new",       icon: Plus, kind: "action" },
  { label: "Create · New Purchase Order", to: "/purchase-orders/new", icon: Plus, kind: "action" },
];

export default function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const { can, roleCodes, isSuperAdmin, financeRestricted } = usePerms();
  const quick = useMemo(() => [
    ...GROUPS.flatMap(group => group.items).filter(item => !item.migration && !item.hideInProduction
      && (!item.superOnly || isSuperAdmin) && navigationAllowed(item, can, roleCodes, financeRestricted))
      .map(item => ({ ...item, kind: "nav" })),
    ...QUICK.filter(item => item.kind === "action" && can(
      { quotations: "quotations", invoices: "invoices", receipts: "receipts", "purchase-orders": "purchase_orders" }[item.to.split("/")[1]], "create")),
  ], [can, roleCodes, isSuperAdmin, financeRestricted]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // reset on open
  useEffect(() => {
    if (open) {
      setQ(""); setResults([]); setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResults([]); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      const promises = [
        can("customers", "view") ? api.get(`/customers?q=${encodeURIComponent(term)}&limit=6`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
        can("invoices", "view") ? api.get(`/documents?doc_type=invoice&q=${encodeURIComponent(term)}&limit=6`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
        can("quotations", "view") ? api.get(`/documents?doc_type=quotation&q=${encodeURIComponent(term)}&limit=6`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
        can("work_orders", "view") ? api.get(`/work-orders?q=${encodeURIComponent(term)}&limit=6`).catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
      ];
      try {
        const [c, i, quo, w] = await Promise.all(promises);
        const items = [
          ...(c.data.items || []).slice(0, 6).map(x => ({
            kind: "customer", id: x.id, label: x.name, sub: x.name_lc || "customer",
            icon: Users, to: `/customers/${x.id}`,
          })),
          ...(w.data.items || []).slice(0, 6).map(x => ({
            kind: "wo", id: x.id, label: x.wo_no || "Work order", sub: x.customer_name || "work order",
            icon: Factory, to: `/work-orders/${x.id}`,
          })),
          ...(i.data.items || []).slice(0, 6).map(x => ({
            kind: "inv", id: x.id, label: x.doc_no || "Invoice", sub: x.customer_name || "invoice",
            icon: FileText, to: `/invoices/${x.id}`,
          })),
          ...(quo.data.items || []).slice(0, 4).map(x => ({
            kind: "quo", id: x.id, label: x.doc_no || "Quotation", sub: x.customer_name || "quotation",
            icon: FileText, to: `/quotations/${x.id}`,
          })),
        ];
        setResults(items);
        setActive(0);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [q, open]);

  // Static list = QUICK when no query
  const items = useMemo(() => {
    if (q.trim()) return [...quick.filter(item => item.label.toLowerCase().includes(q.trim().toLowerCase())), ...results];
    return quick;
  }, [q, results, quick]);

  const choose = useCallback((it) => {
    if (!it) return;
    onClose();
    nav(it.to);
  }, [nav, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
      else if (e.key === "Enter") { e.preventDefault(); choose(items[active]); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, items, active, choose, onClose]);

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  return (
    <>
      <ModalOverlay onClose={onClose} className="f-scrim" data-testid="cmdk-scrim" />
      <div
        className="f-dialog f-dialog--md"
        style={{
          top: "20%", transform: "translate(-50%, 0)",
          width: 640, padding: 0,
          background: "var(--flg-bg-elevated)",
          borderRadius: "var(--flg-radius-xl)",
          boxShadow: "var(--flg-shadow-xl)",
        }}
        data-testid="cmdk-dialog"
      >
        {/* Input */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 20px", borderBottom: "1px solid var(--flg-border)",
        }}>
          <Search size={18} style={{ color: "var(--flg-fg-muted)" }} />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search customers, products, work orders, invoices…"
            data-testid="cmdk-input"
            style={{
              flex: 1, border: 0, outline: 0,
              background: "transparent",
              fontSize: 16, color: "var(--flg-fg)",
              fontFamily: "var(--flg-font-sans)",
            }}
          />
          {loading && <Loader2 size={16} className="f-loading-shimmer" style={{ color: "var(--flg-fg-muted)", animation: "f-spin 1s linear infinite" }} />}
          <span className="f-kbd">ESC</span>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 420, overflowY: "auto", padding: 8 }}>
          {items.length === 0 && !loading && (
            <div style={{ padding: 32, textAlign: "center", color: "var(--flg-fg-muted)" }}>
              No results found for &ldquo;<strong style={{ color: "var(--flg-fg)" }}>{q}</strong>&rdquo;.
            </div>
          )}
          {items.map((it, i) => {
            const Icon = it.icon || ArrowRight;
            const isActive = i === active;
            return (
              <div
                key={`${it.kind}-${it.id || it.to}-${i}`}
                data-idx={i}
                data-testid={`cmdk-item-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(it)}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                  background: isActive ? "var(--flg-bg-subtle)" : "transparent",
                  color: "var(--flg-fg)",
                  transition: "background 0.08s",
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: isActive ? "var(--flg-primary-soft)" : "var(--flg-bg-muted)",
                  color: isActive ? "var(--flg-primary)" : "var(--flg-fg-muted)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.label}
                  </div>
                  <div className="f-caption" style={{ marginTop: 1 }}>{it.sub || (it.kind === "action" ? "Quick action" : "Navigation")}</div>
                </div>
                {isActive && <ArrowRight size={14} style={{ color: "var(--flg-fg-muted)" }} />}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: "10px 16px", borderTop: "1px solid var(--flg-border)",
          background: "var(--flg-bg-subtle)",
          display: "flex", gap: 16, alignItems: "center",
          fontSize: 11, color: "var(--flg-fg-muted)",
        }}>
          <span><span className="f-kbd">↑</span> <span className="f-kbd">↓</span> Navigate</span>
          <span><span className="f-kbd">↵</span> Select</span>
          <span><span className="f-kbd">ESC</span> Close</span>
          <div style={{ marginLeft: "auto" }}>VianBills · ⌘K</div>
        </div>
      </div>
      <style>{`@keyframes f-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
